import axios from '../lib/axios';

// Product variation interface
export interface ProductVariation {
  name: string;
  mrp: number;
  mop: number;
  features: string[];
}

// Product interface
export interface Product {
  _id: string;
  partnerName: string;
  brandName: string;
  name: string;
  modelCode?: string; // Optional modelCode field
  category: string;
  subCategory: string;
  schemeBased: boolean;
  schemes: string[];
  mrpCheck: boolean;
  serialNoValidationLevel: number;
  variations: ProductVariation[];
  createdAt: string;
  updatedAt: string;
  __v: number;
  logs?: string[]; // Logs array
}

// API response interface
export interface ProductApiResponse {
  status: string;
  result: {
    data: {
      totalCount: number;
      totalMatch: number;
      totalPages: number;
      pageNo: number;
      pageSize: number;
      results: Product[];
    }
  }
}

// Params for fetching products
export interface FetchProductsParams {
  page: number;
  pageSize: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  searchQuery?: string;
}

/**
 * Fetch products with pagination, sorting, and optional search
 */
export const fetchProducts = async (params: FetchProductsParams): Promise<ProductApiResponse> => {
  const { page, pageSize, sortBy, sortOrder, searchQuery } = params;
  
  const queryParams = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
    sortBy: sortBy,
    sortOrder: sortOrder
  });
  
  if (searchQuery) {
    queryParams.append('searchQuery', searchQuery);
  }
  
  try {
    const response = await axios.get<ProductApiResponse>(`adminproduct/v1?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

/**
 * Get a single product by ID
 */
export const getProductById = async (productId: string): Promise<Product> => {
  try {
    const response = await axios.get<{ status: string; result: { data: Product } }>(`adminproduct/v1/${productId}`);
    return response.data.result.data;
  } catch (error) {
    console.error(`Error fetching product ${productId}:`, error);
    throw error;
  }
};

/**
 * Create a new product
 */
export const createProduct = async (productData: Omit<Product, '_id' | 'createdAt' | 'updatedAt' | '__v'>) => {
  try {
    console.log('Creating product with data:', JSON.stringify(productData, null, 2));
    
    // Get token from localStorage
    const token = localStorage.getItem('token') || '';
    
    if (!token) {
      console.error('No token found in localStorage');
      throw new Error('Authentication token is missing');
    }
    
    console.log('Using auth token:', token.substring(0, 10) + '...');
    
    // Configure request with timeout and token
    const config = {
      timeout: 30000, // Increase timeout to 30 seconds
      headers: {
        'Content-Type': 'application/json',
        'token': token,
        'Accept': 'application/json'
      }
    };
    
    // Validate the product data
    if (!productData.name || !productData.brandName || !productData.category) {
      throw new Error('Product missing required fields (name, brandName, category)');
    }
    
    if (!productData.variations || productData.variations.length === 0) {
      throw new Error('Product must have at least one variation');
    }
    
    // Log the actual request being made
    console.log('API Request to product/v1:', {
      method: 'POST',
      url: 'adminproduct/v1',
      headers: config.headers,
      data: JSON.stringify(productData)
    });
    
    // Make API request using the axios instance (not direct axios import)
    const response = await axios.post('adminproduct/v1', productData, config);
    
    console.log('Create product response status:', response.status);
    console.log('Create product response data:', response.data);
    
    return response.data;
  } catch (error: any) {
    console.error('Error creating product:', error);
    
    // Enhanced error logging
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Error response data:', error.response.data);
      console.error('Error response status:', error.response.status);
      console.error('Error response headers:', error.response.headers);
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received. Request details:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error message:', error.message);
    }
    
    throw error;
  }
};

/**
 * Update an existing product
 */
export const updateProduct = async (productId: string, productData: Partial<Product>) => {
  try {
    // Ensure productId is a valid string
    if (!productId || typeof productId !== 'string') {
      console.error(`Invalid product ID for update: ${productId}`);
      throw new Error('Invalid product ID');
    }

    console.log(`Updating product with ID: ${productId}`, productData);
    
    // Get token from localStorage
    const token = localStorage.getItem('token') || '';
    
    // Configure request with timeout and token
    const config = {
      timeout: 15000, // 15 seconds timeout
      headers: {
        'Content-Type': 'application/json',
        'token': token
      }
    };
    
    // Log the actual request being made
    console.log('API Request to update product:', {
      method: 'PUT',
      url: `adminproduct/v1?id=${productId}`,
      headers: config.headers,
      data: productData
    });
    
    // Make API request
    const response = await axios.put(`adminproduct/v1?id=${productId}`, productData, config);
    console.log('Update product response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error(`Error updating product ${productId}:`, error);
    
    // Enhanced error logging
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Error response data:', error.response.data);
      console.error('Error response status:', error.response.status);
      console.error('Error response headers:', error.response.headers);
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received. Request details:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error message:', error.message);
    }
    
    throw error;
  }
};

/**
 * Delete a product
 */
export const deleteProduct = async (productId: string) => {
  try {
    // Ensure productId is a valid string
    if (!productId || typeof productId !== 'string') {
      console.error(`Invalid product ID for deletion: ${productId}`);
      throw new Error('Invalid product ID');
    }
    
    console.log(`Deleting product with ID: ${productId}`);
    
    // Get token from localStorage
    const token = localStorage.getItem('token') || '';
    
    // Configure request with timeout and token
    const config = {
      timeout: 15000, // 15 seconds timeout
      headers: {
        'Content-Type': 'application/json',
        'token': token
      }
    };
    
    // Log the actual request being made
    console.log('API Request to delete product:', {
      method: 'DELETE',
      url: `adminproduct/v1?id=${productId}`,
      headers: config.headers
    });
    
    // Make API request
    const response = await axios.delete(`adminproduct/v1?id=${productId}`, config);
    console.log('Delete product response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error(`Error deleting product ${productId}:`, error);
    
    // Enhanced error logging
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Error response data:', error.response.data);
      console.error('Error response status:', error.response.status);
      console.error('Error response headers:', error.response.headers);
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received. Request details:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error message:', error.message);
    }
    
    throw error;
  }
}; 